/**
 * Model Loader - Utility for loading and managing 3D models
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export class ModelLoader {
    constructor() {
        // Shared loading manager for unified progress
        this.loadingManager = new THREE.LoadingManager();
        this.isInitialLoad = true; // 只在初始加载时显示进度
        this.initialLoadComplete = false;

        // Hook global progress to UI if available (仅初始加载)
        this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
            if (!this.isInitialLoad) return; // 跳过游戏中的动态加载

            const percent = Math.round((itemsLoaded / Math.max(1, itemsTotal)) * 100);
            try {
                const ui = window?.gameApp?.uiManager;
                if (ui && typeof ui.updateLoadingProgress === 'function') {
                    ui.updateLoadingProgress(percent);
                }
            } catch (_) {}
        };
        this.loadingManager.onLoad = () => {
            if (!this.isInitialLoad) return;

            try {
                const ui = window?.gameApp?.uiManager;
                if (ui && typeof ui.updateLoadingProgress === 'function') {
                    ui.updateLoadingProgress(100, { finalize: true });
                }
                this.initialLoadComplete = true;
            } catch (_) {}
        };

        this.loader = new GLTFLoader(this.loadingManager);
        this.cache = new Map(); // Cache loaded models
        this.loadingPromises = new Map(); // Track loading promises
    }

    // 标记初始加载完成，后续加载不再显示进度
    finishInitialLoad() {
        this.isInitialLoad = false;
    }

    /**
     * Load a GLB model from the models directory
     * @param {string} modelName - Name of the model file (without extension)
     * @param {Object} options - Loading options
     * @returns {Promise<THREE.Group>} The loaded model
     */
    async loadModel(modelName, options = {}) {
        const {
            scale = 1,
            position = { x: 0, y: 0, z: 0 },
            rotation = { x: 0, y: 0, z: 0 },
            castShadow = true,
            receiveShadow = true
        } = options;

        // Check cache first
        const cacheKey = `${modelName}_${scale}`;
        if (this.cache.has(cacheKey)) {
            const cachedModel = this.cache.get(cacheKey);
            return this.cloneModel(cachedModel, position, rotation, castShadow, receiveShadow);
        }

        // Check if already loading
        if (this.loadingPromises.has(modelName)) {
            const cachedModel = await this.loadingPromises.get(modelName);
            return this.cloneModel(cachedModel, position, rotation, castShadow, receiveShadow);
        }

        // Start loading
        const modelPath = `/models/GLB format/${modelName}.glb`;
        const loadingPromise = this.loadGLB(modelPath, scale);
        this.loadingPromises.set(modelName, loadingPromise);

        try {
            const model = await loadingPromise;
            this.cache.set(cacheKey, model);
            this.loadingPromises.delete(modelName);

            return this.cloneModel(model, position, rotation, castShadow, receiveShadow);
        } catch (error) {
            this.loadingPromises.delete(modelName);
            console.error(`Failed to load model ${modelName}:`, error);
            throw error;
        }
    }

    /**
     * Load GLB file using GLTFLoader
     * @param {string} path - Path to the GLB file
     * @param {number} scale - Scale factor
     * @returns {Promise<THREE.Group>}
     */
    loadGLB(path, scale) {
        return new Promise((resolve, reject) => {
            this.loader.load(
                path,
                (gltf) => {
                    const model = gltf.scene;

                    // Apply scale
                    if (scale !== 1) {
                        model.scale.setScalar(scale);
                    }

                    // Setup shadows for all meshes
                    model.traverse((child) => {
                        if (child.isMesh) {
                            child.castShadow = true;
                            child.receiveShadow = true;
                        }
                    });

                    resolve(model);
                },
                // Per-file progress is handled by LoadingManager; keep this light
                undefined,
                (error) => {
                    console.error(`Error loading ${path}:`, error);
                    reject(error);
                }
            );
        });
    }

    /**
     * Clone a model with specific transformations
     * @param {THREE.Group} originalModel - The original model to clone
     * @param {Object} position - Position coordinates
     * @param {Object} rotation - Rotation angles in radians
     * @param {boolean} castShadow - Whether the model casts shadows
     * @param {boolean} receiveShadow - Whether the model receives shadows
     * @returns {THREE.Group} Cloned model
     */
    cloneModel(originalModel, position, rotation, castShadow, receiveShadow) {
        const clonedModel = originalModel.clone();

        // Set position
        clonedModel.position.set(position.x, position.y, position.z);

        // Set rotation
        clonedModel.rotation.set(rotation.x, rotation.y, rotation.z);

        // Update shadow settings and clone materials to prevent shared material issues
        clonedModel.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = castShadow;
                child.receiveShadow = receiveShadow;

                // Clone materials to ensure each instance has independent materials
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material = child.material.map(mat => mat.clone());
                    } else {
                        child.material = child.material.clone();
                    }
                }
            }
        });

        return clonedModel;
    }

    /**
     * Load multiple models concurrently
     * @param {Array} modelConfigs - Array of model configurations
     * @returns {Promise<Array>} Array of loaded models
     */
    async loadModels(modelConfigs) {
        const promises = modelConfigs.map(config =>
            this.loadModel(config.name, config.options)
        );

        return Promise.all(promises);
    }

    /**
     * Get available model names from the previews
     * @returns {Array<string>} List of available model names
     */
    getAvailableModels() {
        return [
            // Ships and boats
            'ship-small', 'ship-large', 'ship-cargo-a', 'ship-cargo-b', 'ship-cargo-c',
            'ship-ocean-liner', 'ship-ocean-liner-small', 'ship-small-ghost',
            'boat-fan', 'boat-fishing-small', 'boat-house-a', 'boat-house-b', 'boat-house-c', 'boat-house-d',
            'boat-row-large', 'boat-row-small', 'boat-sail-a', 'boat-sail-b',
            'boat-speed-a', 'boat-speed-b', 'boat-speed-c', 'boat-speed-d', 'boat-speed-e',
            'boat-speed-f', 'boat-speed-g', 'boat-speed-h', 'boat-speed-i', 'boat-speed-j',
            'boat-tow-a', 'boat-tow-b', 'boat-tug-a', 'boat-tug-b', 'boat-tug-c',

            // Environment
            'buoy', 'buoy-flag',

            // Cargo and containers
            'cargo-container-a', 'cargo-container-b', 'cargo-container-c',
            'cargo-pile-a', 'cargo-pile-b',

            // Structures
            'gate', 'gate-finish', 'ramp', 'ramp-wide',

            // Arrows and markers
            'arrow', 'arrow-standing'
        ];
    }

    /**
     * Clear the model cache
     */
    clearCache() {
        this.cache.clear();
        console.log('Model cache cleared');
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache statistics
     */
    getCacheStats() {
        return {
            cachedModels: this.cache.size,
            activeLoads: this.loadingPromises.size,
            memoryUsage: this.cache.size * 1024 // Rough estimate
        };
    }
}

// Export singleton instance
export const modelLoader = new ModelLoader();
